import React, {useEffect, useMemo, useState} from 'react';
import {Divider} from 'antd';
import VStack from '../../../../../common/Layout/VStack';
import AppText from '../../../../../common/AppText';
import {fetchApi} from '../../../../../../services/HTTP';
import {t} from '../../../../../../services/i18n';
import {GlobalStyleAttributes} from '../../../../../../styles/GlobalStyles';
import {Colors} from '../../../../../../styles/Colors';
import {sortCourseEventDays} from '../../../../../../services/Helpers';
import TrackCourseSeriesEventRow from '../ChooseTrackCourses/TrackCourseSeriesEventRow';
import ChooseHugToSwapSkeleton from './ChooseHugToSwapSkeleton';

const RequiredCourseFromTrack = ({values, setFieldValue}) => {
    const [hugimList, setHugimList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const selectedCourses = useMemo(() => {
        const selectedById = (values.courses ?? []).reduce((acc, course) => {
            if (course?.series_id) {
                acc[course.series_id] = course;
            }
            return acc;
        }, {});

        return (hugimList ?? []).filter(hug => selectedById[hug.id]).map(hug => ({
                hug,
                courseSelection: selectedById[hug.id],
                seriesEvents: sortCourseEventDays(hug.series_events ?? []),
            }));
    }, [hugimList, values.courses]);

    useEffect(() => {
        fetchHugimForTrack();
    }, []);

    const fetchHugimForTrack = async () => {
        if (!values.locations_box_fk) {
            setHugimList([]);
            setIsLoading(false);
            return;
        }
        try {
            setIsLoading(true);
            const res = await fetchApi('seriesTrack/getHugimForTracks', 'POST', {
                locations_box_id: values.locations_box_fk,
                start_date: values.start_date,
                end_date: values.end_date,
            }, false, true);
            setHugimList(res ?? []);
            setIsLoading(false);
        } catch (error) {
            console.error(error);
            setHugimList([]);
            setIsLoading(false);

        }
    };

    const onRequiredEventChange = (eventId) => {
        const currentIds = values.series_event_ids ?? [];
        const updatedIds = currentIds.includes(eventId) ? currentIds.filter(id => id !== eventId) : [...currentIds, eventId];
        setFieldValue('series_event_ids', updatedIds);
    };

    const isRequiredEventDisabled = ({eventId, seriesEventIds, trackSessionPerWeek, hug, courseSelection}) => {
        const seriesEvents = hug?.series_events ?? [];
        const selectedForSeries = (seriesEventIds ?? []).filter(id =>
            seriesEvents.some(event => event.id === id),
        );
    
        if (selectedForSeries.includes(eventId)) {
            return false;
        }
    
        const limit = courseSelection?.session_per_week;
        if (selectedForSeries.length >= limit) {
            return true;
        }
        return trackSessionPerWeek != null && (seriesEventIds ?? []).length >= trackSessionPerWeek;
    };

    const formatCourseHeader = (hug) => {
        const staff = [hug?.coach, hug?.second_coach].filter(Boolean).map(coach => `${coach.first_name} ${coach.last_name || ''}`.trim()).filter(Boolean).join(', ');
        return [staff, hug?.series_name].filter(Boolean).join(' — ');
    };

    if (isLoading) {
        return <ChooseHugToSwapSkeleton />;
    }

    if (!selectedCourses.length) {
        return (
            <AppText fontSize={GlobalStyleAttributes.fonts.standard16} style={{padding: '2rem 0'}}>
                {t('requiredCourseFromTrackNoCoursesSelected')}
            </AppText>
        );
    }

    return (
        <VStack space={'1.25rem'} flex={1} width={'100%'}>
            {selectedCourses.map(({hug, courseSelection, seriesEvents}, index) => {
                const maxPerWeek = courseSelection?.session_per_week
                return (
                    <React.Fragment key={hug.id}>
                        {index > 0 && <Divider style={{margin: 0}} />}
                        <VStack space={'0.75rem'}>
                            <AppText boldFont fontSize={GlobalStyleAttributes.fonts.heading3}>
                                {formatCourseHeader(hug)}
                            </AppText>
                            <AppText
                                color={Colors.newDesign.darkGrey}
                                fontSize={GlobalStyleAttributes.fonts.standard16}
                            >
                                {t('requiredCourseFromTrackMaxPerWeek', {count: maxPerWeek})}
                            </AppText>
                            <VStack space={'0.35rem'} style={{paddingInlineStart: '0.25rem'}}>
                                {seriesEvents.map(seriesEvent => (
                                    <TrackCourseSeriesEventRow
                                        key={seriesEvent.id}
                                        seriesEvent={seriesEvent}
                                        onSelect={() => onRequiredEventChange(seriesEvent.id)}
                                        isDisabled={isRequiredEventDisabled({
                                            eventId: seriesEvent.id,
                                            seriesEventIds: values.series_event_ids,
                                            trackSessionPerWeek: values.session_per_week,
                                            hug,
                                            courseSelection,
                                        })}
                                        isChecked={(values.series_event_ids ?? []).includes(seriesEvent.id)}
                                    />
                                ))}
                            </VStack>
                        </VStack>
                    </React.Fragment>
                );
            })}
        </VStack>
    );
};

export default RequiredCourseFromTrack;
